import { createSlice } from "@reduxjs/toolkit";

import { clearStore } from "actions/initialization";
import localStorageService from "services/localStorage";
import {
  projectsListThunk,
  fetchProjectThunk,
  fetchProjectByUrlThunk,
  createProjectThunk,
  updateProjectThunk,
  renameProjectThunk,
  deleteProjectThunk,
  exportProjectThunk,
  statsProjectThunk,
  searchProjectsThunk,
  projectsInitialSearchListThunk,
} from "thunks/project";
import {
  GMT_TIME_IDENTIFIER,
  initialProjectListPaginationParams,
  LOCALSTORAGE_ITEMS,
  PAGINATION,
  PER_PAGE_VALUES,
  PROJECTS_ENUMS,
  PROJECTS_SORT_KEYS,
  SORT_DIRECTION,
} from "../constants";

const emptyLoading = {
  list: false,
  fetch: false,
  create: false,
  update: false,
  rename: false,
  delete: false,
  stats: false,
  export: false,
  search: false,
};

export const getDefaultFilters = () => ({
  state: (
    localStorageService.getItem(LOCALSTORAGE_ITEMS.filters.projects.state) || []
  ).length
    ? localStorageService.getItem(LOCALSTORAGE_ITEMS.filters.projects.state)
    : [PROJECTS_ENUMS.state.active],
});

export const getInitialFilters = () => ({
  state:
    localStorageService.getItem(LOCALSTORAGE_ITEMS.filters.projects.state) ||
    [],
});

export const initialState = {
  ...initialProjectListPaginationParams,
  pagination: {
    ...initialProjectListPaginationParams.pagination,
    limit: PER_PAGE_VALUES.projects.includes(
      localStorageService.getItem(LOCALSTORAGE_ITEMS.perPage.projects)
    )
      ? localStorageService.getItem(LOCALSTORAGE_ITEMS.perPage.projects)
      : PAGINATION.projects,
  },
  sort: {
    key: Object.keys(PROJECTS_SORT_KEYS)
      .map((k) => PROJECTS_SORT_KEYS[k].key)
      .includes(
        localStorageService.getItem(LOCALSTORAGE_ITEMS.sort.projects.key)
      )
      ? localStorageService.getItem(LOCALSTORAGE_ITEMS.sort.projects.key)
      : PROJECTS_SORT_KEYS.UPDATED_AT_DESC.key,
    direction: Object.keys(SORT_DIRECTION)
      .map((k) => SORT_DIRECTION[k])
      .includes(
        localStorageService.getItem(LOCALSTORAGE_ITEMS.sort.projects.direction)
      )
      ? localStorageService.getItem(LOCALSTORAGE_ITEMS.sort.projects.direction)
      : SORT_DIRECTION.desc,
  },
  filters: getInitialFilters(),
  ids: [],
  hash: {},
  loading: {
    list: false,
    fetch: false,
    create: false,
    update: false,
    rename: false,
    delete: false,
    stats: false,
    export: false,
    search: false,
  },
  cachedAt: null,
  error: {
    list: null,
    fetch: null,
    create: null,
    update: null,
    rename: null,
    delete: null,
    stats: null,
    export: null,
    search: null,
  },
  search: {
    query: undefined,
    ids: [],
    hash: {},
  },
};

export const projectDTO = (project, prevState) => ({
  ...project,
  createdAtTimestamp: Date.parse(`${project.created_at}${GMT_TIME_IDENTIFIER}`),
  lastRecordingAtTimestamp: Date.parse(
    `${project.last_recording_at}${GMT_TIME_IDENTIFIER}`
  ),
  folders: prevState ? prevState.folders || [] : [],
  stats: {},
  loading: emptyLoading,
});

const projectSlice = createSlice({
  name: "project",
  initialState,
  reducers: {
    projectListSetPaginationData(state, action) {
      const { pagination, sort, filters } = action.payload;
      if (pagination) {
        const { count, currentPage, limit } = pagination;
        if (currentPage) {
          state.pagination.currentPage = currentPage;
          state.pagination.offset = (currentPage - 1) * state.pagination.limit;
        }
        if (count) {
          state.pagination.count = count;
        }
        if (limit) {
          state.pagination.limit = limit;
        }
      }
      if (sort) {
        state.sort = sort;
      }
      if (filters) {
        state.filters = filters;
      }
    },
    projectStatsUpdate(state, action) {
      const { projectId, stats } = action.payload;
      if (state.hash[projectId]) {
        if (state.hash[projectId].stats) {
          state.hash[projectId].stats.conversions = stats;
        } else {
          state.hash[projectId].stats = {};
        }
      }
    },
    clearSearch(state) {
      state.search.ids = [];
      state.search.hash = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // #region pending
      .addCase(projectsListThunk.pending, (state) => {
        state.loading.list = true;
      })
      .addCase(createProjectThunk.pending, (state) => {
        state.loading.create = true;
      })
      .addCase(fetchProjectThunk.pending, (state) => {
        state.loading.fetch = true;
      })
      .addCase(fetchProjectByUrlThunk.pending, (state) => {
        state.loading.fetch = true;
      })
      .addCase(updateProjectThunk.pending, (state, action) => {
        const {
          meta: {
            arg: { projectId },
          },
        } = action;
        if (state.hash[projectId]) {
          state.hash[projectId].loading.update = true;
        }
      })
      .addCase(renameProjectThunk.pending, (state, action) => {
        const {
          meta: {
            arg: { projectId },
          },
        } = action;
        if (state.hash[projectId]) {
          state.hash[projectId].loading.rename = true;
        }
      })
      .addCase(deleteProjectThunk.pending, (state, action) => {
        const {
          meta: {
            arg: { projectId },
          },
        } = action;
        if (state.hash[projectId]) {
          state.hash[projectId].loading.delete = true;
        }
      })
      .addCase(exportProjectThunk.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        if (state.hash[projectId]) {
          state.hash[projectId].loading.export = true;
        }
      })
      .addCase(statsProjectThunk.pending, (state, action) => {
        const {
          meta: {
            arg: { projectIds },
          },
        } = action;
        projectIds.forEach(
          (projectId) => (state.hash[projectId].loading.stats = true)
        );
      })
      .addCase(searchProjectsThunk.pending, (state) => {
        state.loading.search = true;
      })
      .addCase(projectsInitialSearchListThunk.pending, (state) => {
        state.loading.search = true;
      })
      // #endregion
      // #region fulfilled
      .addCase(projectsListThunk.fulfilled, (state, action) => {
        state.loading.list = false;
        const prevState = { ...state.hash };
        state.ids = [];
        state.hash = {};
        const { list, pagination } = action.payload;
        list.forEach((project) => {
          state.ids.push(project.id);
          state.hash[project.id] = projectDTO(project, prevState[project.id]);
        });
        const { limit, ...otherPaginationData } = pagination;
        state.pagination = {
          ...state.pagination,
          ...otherPaginationData,
          totalPages: Math.ceil(otherPaginationData.count / limit) || 1,
          forceFetch: false,
        };
        state.cachedAt = Date.now();
      })
      .addCase(createProjectThunk.fulfilled, (state, action) => {
        state.loading.create = false;
        const project = action.payload;
        const projectId = project.id;
        // state.ids.unshift(projectId);
        state.hash[projectId] = projectDTO(project);
      })
      .addCase(fetchProjectThunk.fulfilled, (state, action) => {
        const project = action.payload;
        state.loading.fetch = false;
        if (!state.ids.includes(project.id)) {
          state.ids.push(project.id);
        }
        state.hash[project.id] = projectDTO(project);
      })
      .addCase(fetchProjectByUrlThunk.fulfilled, (state, action) => {
        const project = action.payload;
        state.loading.fetch = false;
        if (!state.ids.includes(project.id)) {
          state.ids.push(project.id);
        }
        if (!state.search.ids.includes(project.id)) {
          state.search.ids.push(project.id);
        }
        state.hash[project.id] = {
          ...project,
          createdAtTimestamp: Date.parse(
            `${project.created_at}${GMT_TIME_IDENTIFIER}`
          ),
          lastRecordingAtTimestamp: Date.parse(
            `${project.last_recording_at}${GMT_TIME_IDENTIFIER}`
          ),
          folders: [],
          stats: {},
          loading: emptyLoading,
        };
        state.search.hash[project.id] = projectDTO(project);
      })
      .addCase(updateProjectThunk.fulfilled, (state, action) => {
        const { project } = action.payload;
        if (state.hash[project.id]) {
          state.hash[project.id].loading.update = false;
          state.hash[project.id].models = project.models;
        }
      })
      .addCase(renameProjectThunk.fulfilled, (state, action) => {
        const project = action.payload;
        if (state.hash[project.id]) {
          state.hash[project.id].loading.rename = false;
          state.hash[project.id].name = project.name;
        }
        if (state.search.hash[project.id]) {
          state.search.hash[project.id].name = project.name;
        }
      })
      .addCase(deleteProjectThunk.fulfilled, (state, action) => {
        const { projectId } = action.payload;
        if (state.hash[projectId]) {
          state.hash[projectId].loading.delete = false;
          delete state.hash[projectId];
        }
        state.ids = state.ids.filter((id) => id !== projectId);
        // sync pagination
        if (
          state.pagination.currentPage === state.pagination.totalPages &&
          state.pagination.currentPage > 1 &&
          state.ids.length === 0
        ) {
          state.pagination.currentPage = state.pagination.currentPage - 1;
          state.pagination.offset =
            (state.pagination.currentPage - 1) * state.pagination.limit;
        }
        if (
          state.pagination.currentPage !== state.pagination.totalPages &&
          state.ids.length < state.pagination.limit
        ) {
          state.pagination.forceFetch = true;
        }
        if (
          state.pagination.currentPage === 1 &&
          state.ids.length !== state.pagination.count
        ) {
          state.pagination.count = state.ids.length;
        }
      })
      .addCase(exportProjectThunk.fulfilled, (state, action) => {
        const {
          meta: {
            arg: { projectId },
          },
        } = action;
        state.hash[projectId].loading.export = false;
      })
      .addCase(statsProjectThunk.fulfilled, (state, action) => {
        const projects = action.payload;
        state.ids.forEach((id) => {
          if (state.hash[id]) {
            state.hash[id].loading.stats = false;
            state.hash[id].stats = {};
          }
        });
        projects.forEach((project) => {
          if (!state.hash[project.project_id]) {
            state.hash[project.project_id] = { stats: {} };
          }
          state.hash[project.project_id].stats = project;
        });
      })
      .addCase(searchProjectsThunk.fulfilled, (state, action) => {
        state.loading.search = false;
        state.search.ids = [];
        state.search.hash = {};
        const items = action.payload;
        items.forEach((project) => {
          state.search.ids.push(project.id);
          state.search.hash[project.id] = projectDTO(project);
        });
      })
      .addCase(projectsInitialSearchListThunk.fulfilled, (state, action) => {
        state.loading.search = false;
        state.search.ids = [];
        state.search.hash = {};
        const { list } = action.payload;
        list.forEach((project) => {
          state.search.ids.push(project.id);
          state.search.hash[project.id] = projectDTO(project);
        });
      })
      // #endregion
      // #region rejected
      .addCase(projectsListThunk.rejected, (state, action) => {
        // projectsList is dispatched without a projectId, so the previous
        // state.hash[projectId] access threw on every failed list fetch.
        // Match the pending handler, which sets the global loading.list.
        const { error } = action;
        state.loading.list = false;
        state.error.list = error;
      })
      .addCase(createProjectThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.create = false;
        state.error.create = error;
      })
      .addCase(fetchProjectThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.fetch = false;
        state.error.fetch = error;
      })
      .addCase(fetchProjectByUrlThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.fetch = false;
        state.error.fetch = error;
      })
      .addCase(updateProjectThunk.rejected, (state, action) => {
        const {
          meta: {
            arg: { projectId },
          },
          error,
        } = action;
        if (state.hash[projectId]) {
          state.hash[projectId].loading.update = false;
        }
        state.error.update = error;
      })
      .addCase(renameProjectThunk.rejected, (state, action) => {
        const {
          meta: {
            arg: { projectId },
          },
          error,
        } = action;
        if (state.hash[projectId]) {
          state.hash[projectId].loading.rename = false;
        }
        state.error.rename = error;
      })
      .addCase(deleteProjectThunk.rejected, (state, action) => {
        const {
          meta: {
            arg: { projectId },
          },
          error,
        } = action;
        if (state.hash[projectId]) {
          state.hash[projectId].loading.delete = false;
        }
        state.error.delete = error;
      })
      .addCase(exportProjectThunk.rejected, (state, action) => {
        const {
          meta: {
            arg: { projectId },
          },
          error,
        } = action;
        if (state.hash[projectId]) {
          state.hash[projectId].loading.export = false;
        }
        state.error.export = error;
      })
      .addCase(statsProjectThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.stats = false;
        state.error.stats = error;
      })
      .addCase(searchProjectsThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.search = false;
        state.error.search = error;
      })
      .addCase(projectsInitialSearchListThunk.rejected, (state, action) => {
        const { error } = action;
        state.loading.search = false;
        state.error.search = error;
      })
      // #endregion
      .addCase(clearStore, () => initialState);
  },
});

export const actions = projectSlice.actions;

export default projectSlice.reducer;
